import { useState } from 'react'
import { Link } from 'react-router-dom'
import { mapFirebaseAuthError } from '@/auth/errors'
import { sendPasswordReset } from '@/auth/firebase'
import { useTranslation } from '@/i18n'

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await sendPasswordReset(email)
      setSent(true)
    } catch (err) {
      const code = (err as { code?: string }).code ?? ''
      if (code === 'auth/user-not-found' || code === 'auth/invalid-email') {
        // Don't reveal whether an account exists
        setSent(true)
      } else {
        setError(t(mapFirebaseAuthError(err, 'passwordReset')))
      }
    } finally {
      setLoading(false)
    }
  }

  if (sent) {
    return (
      <div className="text-center">
        <p className="mb-2 text-3xl">📬</p>
        <h2 className="mb-2 text-lg font-semibold text-surface-100">
          {t('Check je inbox')}
        </h2>
        <p className="mb-6 text-sm text-surface-400">
          {t('Als er een account bestaat voor {email}, ontvang je een e-mail met een resetlink.', { email })}
        </p>
        <Link to="/login" className="text-sm text-accent-400 hover:text-accent-300">
          {t('Terug naar inloggen')}
        </Link>
      </div>
    )
  }

  return (
    <div>
      <h2 className="mb-2 text-xl font-semibold text-surface-100">
        {t('Wachtwoord vergeten')}
      </h2>
      <p className="mb-6 text-sm text-surface-400">
        {t('Vul je e-mailadres in en we sturen je een resetlink.')}
      </p>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm text-surface-300">
            {t('E-mailadres')}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            className="w-full rounded-xl border border-surface-700 bg-surface-800 px-4 py-3 text-sm text-surface-100 placeholder-surface-500 transition focus:border-accent-500 focus:outline-none disabled:opacity-50"
          placeholder={t('naam@voorbeeld.nl')}
          />
        </div>

        {error && (
          <p className="rounded-lg bg-red-950/60 px-4 py-2.5 text-sm text-red-400">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading || !email}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading && (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-accent-300 border-t-white" />
          )}
          {t('Resetlink versturen')}
        </button>

        <Link
          to="/login"
          className="text-center text-sm text-surface-500 hover:text-surface-300"
        >
          {t('Terug naar inloggen')}
        </Link>
      </form>
    </div>
  )
}
